import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  MenuOutlined,
  HolderOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Tooltip } from "@mui/material";

import { setGridConfig } from "../../../redux/actions/WidgetMaker/GraphConfig";

import "./AdvGridStyle/advGridColumnSetting.css";
import { callNotification } from "../../../redux/constants";

function AdvGridColumnsSetting() {
  const listRef = useRef(null);
  const loadComponent = useRef(true);
  const dispatch = useDispatch();

  const [columnsItems, setColumnsItems] = useState([]);
  const [openDropDown, setOpenDropDown] = useState(false);
  const [listPosition, setListPosition] = useState({
    left: 0,
    bottom: 0,
  });

  const columns = useSelector(
    (state) =>
      state?.WidgetAPI?.widget_maker_graph_data?.data?.grid?.[0].columns
  );

  const hideColumns = useSelector(
    (state) =>
      state?.WidgetAPI?.widget_maker_graph_data?.data?.grid?.[0].hide_columns
  );

  const gridConfig = useSelector(
    (state) =>
      state?.GraphConfig?.widgetGraphConfig?.chart_settings?.grid_config ||
      state?.GraphConfig?.widgetGraphConfig?.grid_config ||
      {}
  );

  // here compare added new columns
  const compareUpdatedColumns = () => {
    const oldColumns = columnsItems?.map((col) => col.title);
    const updatedColumns = [];
    columns?.forEach((col) => {
      if (col?.children) {
        updatedColumns.push(col?.title);
      } else {
        updatedColumns.push(col?.title);
      }
    });
    let compare =
      updatedColumns?.length === oldColumns?.length &&
      updatedColumns?.every((value) => oldColumns.includes(value));
    return !compare;
  };

  const compareInitialUpdatedColumns = (configColumns) => {
    const oldColumns = configColumns?.map((col) => col.title);
    const updatedColumns = [];
    columns?.forEach((col) => {
      if (col?.children) {
        updatedColumns.push(col?.title);
      } else {
        updatedColumns.push(col?.title);
      }
    });
    let compare =
      updatedColumns?.length === oldColumns?.length &&
      updatedColumns?.every((value) => oldColumns.includes(value));
    return !compare;
  };

  const ensureAtLeastOneIntAndVarchar = (column) => {
    const tempColumns = [...column];
    const intCount = column.filter(
      (col) => col.datatype === "int" && col.visible
    ).length;
    const varcharCount = column.filter(
      (col) => col.datatype === "varchar" && col.visible
    ).length;
    if (intCount < 1 || varcharCount < 1) {
      for (let i = 0; i < tempColumns.length; i++) {
        if (intCount < 1 && tempColumns[i].datatype === "int") {
          tempColumns[i].visible = true;
          break;
        }
      }
    }
    if (varcharCount < 1) {
      for (let i = 0; i < tempColumns.length; i++) {
        if (varcharCount < 1 && tempColumns[i].datatype === "varchar") {
          tempColumns[i].visible = true;
          break;
        }
      }
    }
    return tempColumns;
  };

  const columnReArranging = (result, config) => {
    let presentCol = [];
    const changeColumnsSetting = config.some((col) => {
      return col?.change;
    });

    if (!changeColumnsSetting) {
      const columnAddSettings = result.map((col) => {
        const colData = config.find((item) => item.title === col.title);
        if (colData) {
          return {
            ...colData,
          };
        } else {
          return col;
        }
      });
      return columnAddSettings;
    }

    config.forEach((col) => {
      const found = result.find((item) => item.title === col.title);
      if (found) {
        presentCol.push({
          ...found,
          visible: col?.visible,
          change: col.change,
        });
      }
    });

    let newCol = result.filter((col) => {
      for (let i = 0; i < presentCol.length; i++) {
        if (presentCol[i].title === col.title) {
          return false;
        }
      }
      return true;
    });

    newCol.forEach((col) => {
      if (col.datatype === "int") {
        presentCol.push(col);
      } else {
        presentCol.unshift(col);
      }
    });

    return presentCol;
  };

  useEffect(() => {
    if (
      compareUpdatedColumns() ||
      JSON.stringify(columnsItems) !== JSON.stringify(gridConfig?.columnConfig)
    ) {
      // set get data from API from first render
      if (
        loadComponent.current &&
        gridConfig?.columnConfig &&
        !compareInitialUpdatedColumns(gridConfig?.columnConfig)
      ) {
        setColumnsItems(gridConfig?.columnConfig);
        loadComponent.current = false;
        return;
      } else if (
        gridConfig?.columnConfig &&
        gridConfig?.columnConfig?.length > 0 &&
        !compareUpdatedColumns()
      ) {
        // after refresh grid with same columns
        setColumnsItems(gridConfig?.columnConfig);
        return;
      } else if (columns?.length > 0 && compareUpdatedColumns()) {
        // when add new columns
        let result = [];
        columns
          ?.filter((value) => value?.title !== "row_span")
          .forEach((value) => {
            if (value?.children) {
              result.push({
                title: value?.title,
                visible: true,
                datatype: value.datatype,
                change: false,
              });
            } else {
              result.push({
                title: value?.title,
                visible: true,
                change: false,
                ...value,
              });
            }
          });
        // hide columns get data from API call
        if (Array.isArray(hideColumns) && hideColumns?.length > 0) {
          result = result.filter((col) => {
            return !hideColumns.includes(col.title);
          });
        }
        if (gridConfig?.columnConfig?.length > 0) {
          result = columnReArranging(result, gridConfig?.columnConfig);
        }
        result = ensureAtLeastOneIntAndVarchar(result);
        setColumnsItems(result);
      }
    } else if (gridConfig?.columnConfig?.length === 0 && columns?.length > 0) {
      //Create Widget Condition gridConfig is undefined no data
      let result = [];
      columns
        ?.filter((value) => value?.title !== "row_span")
        .forEach((value) => {
          if (value?.children) {
            result.push({
              title: value?.title,
              visible: true,
              datatype: value.datatype,
            });
          } else {
            result.push({
              title: value?.title,
              visible: true,
              ...value,
            });
          }
        });
      if (Array.isArray(hideColumns) && hideColumns?.length > 0) {
        result = result.filter((col) => {
          return !hideColumns.includes(col.title);
        });
      }
      setColumnsItems(result);
    }
  }, [columns, hideColumns]);

  const outsideClick = useCallback(() => {
    setOpenDropDown(false);
  }, []);

  useEffect(() => {
    function handleClick(event) {
      // If click is outside the referenced element
      if (listRef.current && !listRef.current.contains(event.target)) {
        outsideClick();
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [listRef, outsideClick]);

  const handleOpen = (e) => {
    e.stopPropagation();
    const { bottom, right } = listRef?.current?.getBoundingClientRect();
    const left = window.innerWidth - right;
    setListPosition({
      left,
      bottom,
    });
    setOpenDropDown((pre) => !pre);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return; // dropped outside list
    const reordered = Array.from(columnsItems);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, { ...removed, change: true });
    setColumnsItems(reordered);
  };

  const handleRemoveColumns = (config, attributeToRemove) => {
    const columnToRemove = config.find(
      (col) => col.title === attributeToRemove
    );
    if (!columnToRemove) return false; // column not found

    const intCount = config.filter(
      (col) => col.datatype === "int" && col.visible
    ).length;
    const varcharCount = config.filter(
      (col) => col.datatype === "varchar" && col.visible
    ).length;

    if (
      columnToRemove.visible &&
      columnToRemove.datatype === "int" &&
      intCount <= 1
    ) {
      callNotification(
        "At least one text and one numeric column must be visible.",
        "error"
      );
      return false;
    }
    if (
      columnToRemove.visible &&
      columnToRemove.datatype === "varchar" &&
      varcharCount <= 1
    ) {
      callNotification("At least one text column must be visible.", "error");
      return false;
    }

    return true;
  };

  const handleVisibility = (title) => {
    if (!handleRemoveColumns(columnsItems, title)) return;
    const result = columnsItems.map((col) => {
      if (col.title === title) {
        return {
          ...col,
          visible: !col.visible,
        };
      } else {
        return col;
      }
    });
    setColumnsItems(result);
  };

  useEffect(() => {
    if (
      columnsItems?.length > 0 &&
      JSON.stringify(columnsItems) !== JSON.stringify(gridConfig?.columnConfig)
    ) {
      // set columns info in gridConfig;
      dispatch(setGridConfig({ ...gridConfig, columnConfig: columnsItems }));
    }
  }, [columnsItems, gridConfig]);

  return (
    <div ref={listRef} className="parent">
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: "#2d3e5c", // background color
              color: "#fff", // text color
              fontSize: "14px", // font size
              padding: "8px 12px", // padding inside tooltip
              borderRadius: "8px", // rounded corners
              boxShadow: 3, // elevation
            },
          },
        }}
        title="Columns Setting"
      >
        <div style={{ cursor: "pointer" }} onClick={handleOpen}>
          <MenuOutlined />
        </div>
      </Tooltip>

      {openDropDown && columnsItems.length !== 0 && (
        <div
          className="list"
          style={{
            right: `${listPosition.left}px`,
            top: `${listPosition.bottom}px`,
          }}
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            {columnsItems.map((item, index) => (
              <Droppable
                key={`droppable-list-${index}`}
                droppableId={`droppable-list-${index}`}
                isDropDisabled={false}
                isCombineEnabled={false}
                ignoreContainerClipping={false}
                direction="vertical"
              >
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="droppable"
                  >
                    <Draggable
                      key={`draggable-item-${index}`}
                      draggableId={`draggable-item-${index}`}
                      index={index}
                    >
                      {(provided) => {
                        const transform =
                          provided.draggableProps?.style?.transform;
                        if (transform) {
                          const t = transform.split(",")[1];
                          // eslint-disable-next-line no-param-reassign
                          provided.draggableProps.style.transform = `translate(0px,${t}`;
                        }
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                            style={{
                              margin: "0",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <div className="columnInfoWrapper">
                              <div className="columnInfo">
                                <HolderOutlined />
                                <span className="title">{item.title}</span>
                              </div>
                              <div
                                onClick={() => handleVisibility(item.title)}
                                className="visibility"
                              >
                                {item.visible ? (
                                  <Tooltip
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "#2d3e5c", // background color
                                          color: "#fff", // text color
                                          fontSize: "14px", // font size
                                          padding: "8px 12px", // padding inside tooltip
                                          borderRadius: "8px", // rounded corners
                                          boxShadow: 3, // elevation
                                        },
                                      },
                                    }}
                                    title="Click here to hide column"
                                  >
                                    <EyeOutlined className="visibleColumn" />
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "#2d3e5c", // background color
                                          color: "#fff", // text color
                                          fontSize: "14px", // font size
                                          padding: "8px 12px", // padding inside tooltip
                                          borderRadius: "8px", // rounded corners
                                          boxShadow: 3, // elevation
                                        },
                                      },
                                    }}
                                    title="Click here to unhide column"
                                  >
                                    <EyeInvisibleOutlined className="hideColumn" />
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </Draggable>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
          </DragDropContext>
        </div>
      )}
    </div>
  );
}

export default AdvGridColumnsSetting;
